import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { hasFileExtension } from "@/lib/utils/file";
import type { ViewerAsset } from "@/lib/step-visualization";
import type { ResolvedViewerAsset } from "@/lib/forming/viewer-asset-state";
import type { ColorMap } from "@/lib/shaders/colormaps";
import type { FenceView, SeamFenceStroke } from "@/lib/utils/seam-fence";
import type { MeshFootprint } from "@/lib/utils/mesh-silhouette";
import type { BufferGeometry } from "three";
import { discardStoredScene } from "./discard-stored-scene";

discardStoredScene();

export type LoadingState = "idle" | "loading" | "success" | "error";

/**
 * Which part (and, where known, which operation) a scene file belongs to. The viewer draws from
 * one `files` map shared by every step, so without an owner recorded on each file nothing can
 * tell a leftover mesh from the one the current step seeded — which is how a foreign part's
 * mesh ended up rendered alongside the current one (SW-1676).
 */
export type SceneScope = {
  partId: string;
  operationId?: string;
};

type SceneFile = {
  id: string;
  md5: string;
  name: string;
} & SceneScope;

/** Tag a file attachment with the scene it belongs to, dropping fields the viewer never reads. */
export function sceneFile(
  file: { id: string; md5: string; name: string },
  scope: SceneScope
): SceneFile {
  return { id: file.id, md5: file.md5, name: file.name, ...scope };
}

/**
 * A file belongs to exactly one scene, so the operation has to match exactly — including both
 * sides having none, which is how part-level views (a file preview, an MCP show-file) are scoped.
 *
 * Letting an operation-less file pass any operation of its part looks harmless but is not: the
 * deferred `vf` restore runs ~1s after a step has seeded its own mesh, so a part-level leftover
 * would be toggled back on top of it and two meshes would draw again (SW-1676, verified against
 * a real part in the browser).
 */
export function isFileInScope(file: SceneFile, scope: SceneScope): boolean {
  return file.partId === scope.partId && file.operationId === scope.operationId;
}

export type VisualizationError = {
  fileId: string;
  fileName: string;
  message: string;
  code: "unavailable" | "error";
};

export type ComparisonAsset = {
  md5: string;
  url: string;
  label: string;
  colorMap: ColorMap;
};

export type PreviewAsset = {
  kind: "stl" | "slice-persisted" | "path-glb";
  fileId: string;
  md5: string;
  name: string;
  projectId: string;
  partId: string;
  procedureId?: string;
  operationId?: string;
  url?: string;
};

type SceneFileState = {
  files: Record<string, SceneFile>;
  visibleFileIds: string[];
  selectedFileId: string | null;
  sliceFileId: string | null;
};

/** Delete `fileIds` along with every reference the rest of the scene still holds to them. */
function dropFiles(state: SceneFileState, fileIds: string[]) {
  // Returning before touching the draft keeps a no-op call from producing a new state, which
  // would re-render every subscriber on each scope check.
  if (fileIds.length === 0) return;

  for (const fileId of fileIds) {
    delete state.files[fileId];
  }
  state.visibleFileIds = state.visibleFileIds.filter((id) => state.files[id]);
  if (state.selectedFileId && !state.files[state.selectedFileId]) {
    state.selectedFileId = null;
  }
  if (state.sliceFileId && !state.files[state.sliceFileId]) {
    state.sliceFileId = null;
  }
}

/**
 * SpaceMouse connection lifecycle (SW-1424). "unavailable" means the user opted in
 * but the 3DxWare driver/proxy could not be reached — distinct from "error" (the
 * vendored SDK script itself failed to load).
 */
export type SpaceMouseStatus = "idle" | "connecting" | "connected" | "unavailable" | "error";

/** What the Slice step's viewer draws; both on overlays the slices on the mesh (SW-1816). */
export type SliceLayers = { mesh: boolean; slices: boolean };
export type SliceLayer = keyof SliceLayers;

const DEFAULT_SLICE_LAYERS: SliceLayers = { mesh: false, slices: true };

export const useViewer3DStore = create<{
  files: Record<string, SceneFile>;
  visibleFileIds: string[];
  selectedFileId: string | null;
  sliceFileId: string | null;
  visualizationError: VisualizationError | null;
  hoveredRegionIndex: number | null;
  focusedRegionIndex: number | null;
  cameraResetAt: number | null;
  frameObjectAt: number | null;
  grabRequestedAt: number | null;
  /**
   * How the pending grab was requested. "pointer" (clicking the part itself) means the click that
   * begins the grab must not also confirm it; "control" (the toolbar button, or the `g` key) has no
   * initiating canvas click to suppress.
   */
  grabRequestedVia: "control" | "pointer" | null;
  /** A pending offset-adjust gesture requested from an offset band edge handle (or the toolbar). */
  offsetGrabRequestedAt: number | null;
  /** Which offset axis the pending gesture drives: an edge handle picks "x" / "y"; "common" is uniform. */
  offsetGrabRequestedAxis: "common" | "x" | "y" | null;
  displayAsset: ViewerAsset | null;
  // Set by the active Slice/Path panel: the resolved render decision the viewer draws. Null on other
  // steps, which fall back to displayAsset.
  resolvedAsset: ResolvedViewerAsset | null;
  previewAsset: PreviewAsset | null;
  comparison: ComparisonAsset[] | null;
  /** True while a file preview (FileDataView) covers the viewport; the viewer HUD stands down. */
  viewportCovered: boolean;
  /** True while the viewer is in Blender-style fly navigation (SW-1411). Transient. */
  flyMode: boolean;
  /** True while the user has opted into SpaceMouse navigation (SW-1424). Transient. */
  spaceMouse: boolean;
  /** True while the Place Part grab tool is dragging the part; disables OrbitControls. Transient. */
  grabbing: boolean;
  /** True while the Place Part offset tool is dragging a band edge; disables OrbitControls. Transient. */
  offsetGrabbing: boolean;
  /** The axis the grab tool is locked to, driving its in-scene guideline; null when unlocked. */
  grabAxis: "x" | "y" | null;
  /** Connection state of the SpaceMouse driver/proxy (SW-1424). Transient. */
  spaceMouseStatus: SpaceMouseStatus;
  /** True when the viewer renders with an orthographic (vs perspective) camera. */
  orthographic: boolean;
  /** True while spooky mode drapes cobwebs (and their spider) on the part. Transient. */
  spookyMode: boolean;
  /** True while the seam fence drawing mode is active (ML slice form). Transient. */
  seamDrawing: boolean;
  /** True while a fence stroke is being dragged; disables OrbitControls. Transient. */
  seamStrokeActive: boolean;
  /** One-shot request to align the camera to the slicing direction. Transient. */
  seamDrawAlignAt: number | null;
  /** Slicing direction for the pending align: false = top-down (0,0,-1). Transient. */
  seamDrawAlignFlip: boolean;
  /** Drawn fence strokes; the ML slice form mirrors these into its params. Transient. */
  seamFences: SeamFenceStroke[];
  /** Whether drawn seams render in the viewport; auto-hidden once a slice result lands. Transient. */
  seamFencesVisible: boolean;
  /** Seam hovered in the form's list, highlighted in the viewport. Transient. */
  hoveredSeamFenceId: number | null;
  /** The part's rasterized footprint; strokes clip to it. Transient. */
  seamDrawFootprint: MeshFootprint | null;
  /** The part's geometry; strokes pick and drape on its surface via BVH raycasts. Transient. */
  seamDrawGeometry: BufferGeometry | null;
  /**
   * The Slice step's viewer layers: the mesh, the last sliced result, or both overlaid (SW-1816).
   * Display-only, and seam drawing leaves them as the user set them: redrawing seams after a slice
   * needs the slices in view (Rajai, SW-1816 / IB-732). Transient.
   */
  sliceLayers: SliceLayers;
  /** The Slice step has a sliced result the layer toggles can switch between. Transient. */
  slicePreviewAvailable: boolean;
  /** The shown slice no longer matches the form's parameters (the step is Editing). Transient. */
  slicePreviewStale: boolean;

  startSeamDraw: (flip: boolean) => void;
  endSeamDraw: () => void;
  beginSeamFence: (view: FenceView) => void;
  appendSeamFencePoint: (point: [number, number]) => void;
  endSeamFence: () => void;
  /** Commit the open stroke as zero or more footprint-clipped strokes. */
  replaceOpenSeamFence: (pointLists: [number, number][][]) => void;
  removeSeamFence: (id: number) => void;
  clearSeamFences: () => void;
  hydrateSeamFences: (strokes: SeamFenceStroke[]) => void;
  setSeamFencesVisible: (visible: boolean) => void;
  setHoveredSeamFence: (id: number | null) => void;
  setSeamDrawSurface: (footprint: MeshFootprint | null, geometry: BufferGeometry | null) => void;
  toggleSliceLayer: (layer: SliceLayer) => void;
  /** Bring the slices back into view, e.g. once a new slice lands. */
  showSlices: () => void;
  setSlicePreviewStatus: (status: { available: boolean; stale: boolean }) => void;

  clearScene: () => void;
  setFlyMode: (active: boolean) => void;
  setOrthographic: (active: boolean) => void;
  setSpookyMode: (active: boolean) => void;
  setSpaceMouse: (active: boolean) => void;
  setSpaceMouseStatus: (status: SpaceMouseStatus) => void;
  requestCameraReset: () => void;
  requestFrameObject: () => void;
  requestGrab: (via?: "control" | "pointer") => void;
  requestOffsetGrab: (axis?: "common" | "x" | "y") => void;
  setGrabbing: (grabbing: boolean) => void;
  setOffsetGrabbing: (offsetGrabbing: boolean) => void;
  setGrabAxis: (axis: "x" | "y" | null) => void;
  addFile: (file: SceneFile) => void;
  /** Add a file and make it visible alongside what is already shown (unlike `setSliceFile`). */
  showFile: (file: SceneFile) => void;
  setSliceFile: (file: SceneFile | null) => void;
  /** Drop every file outside `scope` — call when the viewed part or operation changes. */
  setScope: (scope: SceneScope) => void;
  toggleFileVisibility: (fileId: string) => void;
  selectFile: (fileId: string) => void;
  deselectFile: () => void;
  toggleSelectFile: (fileId: string) => void;
  removeFile: (fileId: string) => void;
  setVisualizationError: (error: VisualizationError | null) => void;
  setHoveredRegionIndex: (index: number | null) => void;
  setFocusedRegionIndex: (index: number | null) => void;
  setDisplayAsset: (asset: ViewerAsset | null) => void;
  setResolvedAsset: (asset: ResolvedViewerAsset | null) => void;
  setPreviewAsset: (asset: PreviewAsset | null) => void;
  setViewportCovered: (covered: boolean) => void;
  setComparison: (assets: ComparisonAsset[] | null) => void;
  reset: () => void;
}>()(
  immer(
    devtools(
      (set) => ({
        files: {},
        visibleFileIds: [],
        selectedFileId: null,
        sliceFileId: null,
        visualizationError: null,
        hoveredRegionIndex: null,
        focusedRegionIndex: null,
        cameraResetAt: null,
        frameObjectAt: null,
        grabRequestedAt: null,
        grabRequestedVia: null,
        offsetGrabRequestedAt: null,
        offsetGrabRequestedAxis: null,
        displayAsset: null,
        resolvedAsset: null,
        previewAsset: null,
        comparison: null,
        viewportCovered: false,
        flyMode: false,
        spaceMouse: false,
        grabbing: false,
        offsetGrabbing: false,
        grabAxis: null,
        spaceMouseStatus: "idle",
        orthographic: false,
        spookyMode: false,
        seamDrawing: false,
        seamStrokeActive: false,
        seamDrawAlignAt: null,
        seamDrawAlignFlip: false,
        seamFences: [],
        seamFencesVisible: true,
        hoveredSeamFenceId: null,
        seamDrawFootprint: null,
        seamDrawGeometry: null,
        sliceLayers: DEFAULT_SLICE_LAYERS,
        slicePreviewAvailable: false,
        slicePreviewStale: false,

        startSeamDraw: (flip) =>
          set(
            (state) => {
              state.seamDrawing = true;
              state.seamFencesVisible = true;
              state.seamDrawAlignAt = Date.now();
              state.seamDrawAlignFlip = flip;
            },
            false,
            { type: "startSeamDraw", flip }
          ),

        endSeamDraw: () =>
          set(
            (state) => {
              // Finalize any stroke still open (e.g. a polyline mid-construction).
              if (state.seamStrokeActive) {
                const last = state.seamFences[state.seamFences.length - 1];
                if (last && last.points.length < 2) state.seamFences.pop();
                state.seamStrokeActive = false;
              }
              state.seamDrawing = false;
              state.seamDrawAlignAt = null;
            },
            false,
            { type: "endSeamDraw" }
          ),

        beginSeamFence: (view) =>
          set(
            (state) => {
              const nextId = state.seamFences.reduce((max, f) => Math.max(max, f.id + 1), 0);
              state.seamFences.push({ id: nextId, view, points: [] });
              state.seamStrokeActive = true;
            },
            false,
            { type: "beginSeamFence" }
          ),

        appendSeamFencePoint: (point) =>
          set(
            (state) => {
              const stroke = state.seamFences[state.seamFences.length - 1];
              if (!stroke) return;
              const prev = stroke.points[stroke.points.length - 1];
              // A double-click's second click lands on the first; one vertex is enough.
              if (prev && prev[0] === point[0] && prev[1] === point[1]) return;
              stroke.points.push(point);
            },
            false,
            { type: "appendSeamFencePoint" }
          ),

        endSeamFence: () =>
          set(
            (state) => {
              if (!state.seamStrokeActive) return;
              state.seamStrokeActive = false;
              const last = state.seamFences[state.seamFences.length - 1];
              // A fence needs a segment to sweep; a lone vertex is not a stroke.
              if (last && last.points.length < 2) state.seamFences.pop();
            },
            false,
            { type: "endSeamFence" }
          ),

        replaceOpenSeamFence: (pointLists) =>
          set(
            (state) => {
              if (!state.seamStrokeActive) return;
              state.seamStrokeActive = false;
              const open = state.seamFences.pop();
              if (!open) return;
              let nextId = state.seamFences.reduce((max, f) => Math.max(max, f.id + 1), 0);
              for (const points of pointLists) {
                if (points.length < 2) continue;
                state.seamFences.push({ id: nextId++, view: open.view, points });
              }
            },
            false,
            { type: "replaceOpenSeamFence", count: pointLists.length }
          ),

        removeSeamFence: (id) =>
          set(
            (state) => {
              state.seamFences = state.seamFences.filter((f) => f.id !== id);
              if (state.hoveredSeamFenceId === id) state.hoveredSeamFenceId = null;
            },
            false,
            { type: "removeSeamFence", id }
          ),

        clearSeamFences: () =>
          set(
            (state) => {
              state.seamFences = [];
              state.seamStrokeActive = false;
              state.hoveredSeamFenceId = null;
            },
            false,
            { type: "clearSeamFences" }
          ),

        hydrateSeamFences: (strokes) =>
          set(
            (state) => {
              state.seamFences = strokes;
              state.hoveredSeamFenceId = null;
            },
            false,
            { type: "hydrateSeamFences", count: strokes.length }
          ),

        toggleSliceLayer: (layer) =>
          set(
            (state) => {
              const next = { ...state.sliceLayers, [layer]: !state.sliceLayers[layer] };
              // The last visible layer stays on: an empty viewer shows nothing to toggle back to.
              if (next.mesh || next.slices) state.sliceLayers = next;
            },
            false,
            { type: "toggleSliceLayer", layer }
          ),

        showSlices: () =>
          set(
            (state) => {
              state.sliceLayers.slices = true;
            },
            false,
            { type: "showSlices" }
          ),

        setSlicePreviewStatus: ({ available, stale }) =>
          set(
            (state) => {
              state.slicePreviewAvailable = available;
              state.slicePreviewStale = stale;
            },
            false,
            { type: "setSlicePreviewStatus", available, stale }
          ),

        setSeamFencesVisible: (visible) =>
          set(
            (state) => {
              state.seamFencesVisible = visible;
            },
            false,
            { type: "setSeamFencesVisible", visible }
          ),

        setHoveredSeamFence: (id) =>
          set(
            (state) => {
              state.hoveredSeamFenceId = id;
            },
            false,
            { type: "setHoveredSeamFence", id }
          ),

        setSeamDrawSurface: (footprint, geometry) =>
          set(
            (state) => {
              state.seamDrawFootprint = footprint;
              state.seamDrawGeometry = geometry;
            },
            false,
            { type: "setSeamDrawSurface", present: footprint !== null }
          ),

        setOrthographic: (active) =>
          set(
            (state) => {
              state.orthographic = active;
            },
            false,
            { type: "setOrthographic", active }
          ),

        setSpookyMode: (active) =>
          set(
            (state) => {
              state.spookyMode = active;
            },
            false,
            { type: "setSpookyMode", active }
          ),

        setFlyMode: (active) =>
          set(
            (state) => {
              state.flyMode = active;
              if (active) state.orthographic = false;
            },
            false,
            { type: "setFlyMode", active }
          ),

        setSpaceMouse: (active) =>
          set(
            (state) => {
              state.spaceMouse = active;
              if (!active) state.spaceMouseStatus = "idle";
            },
            false,
            { type: "setSpaceMouse", active }
          ),

        requestGrab: (via = "control") =>
          set(
            (state) => {
              state.grabRequestedAt = Date.now();
              state.grabRequestedVia = via;
            },
            false,
            { type: "requestGrab", via }
          ),

        requestOffsetGrab: (axis = "common") =>
          set(
            (state) => {
              state.offsetGrabRequestedAt = Date.now();
              state.offsetGrabRequestedAxis = axis;
            },
            false,
            { type: "requestOffsetGrab", axis }
          ),

        setGrabbing: (grabbing) =>
          set(
            (state) => {
              state.grabbing = grabbing;
            },
            false,
            { type: "setGrabbing", grabbing }
          ),

        setOffsetGrabbing: (offsetGrabbing) =>
          set(
            (state) => {
              state.offsetGrabbing = offsetGrabbing;
            },
            false,
            { type: "setOffsetGrabbing", offsetGrabbing }
          ),

        setGrabAxis: (grabAxis) =>
          set(
            (state) => {
              state.grabAxis = grabAxis;
            },
            false,
            { type: "setGrabAxis", grabAxis }
          ),

        setSpaceMouseStatus: (status) =>
          set(
            (state) => {
              state.spaceMouseStatus = status;
            },
            false,
            { type: "setSpaceMouseStatus", status }
          ),

        requestCameraReset: () =>
          set(
            (state) => {
              state.cameraResetAt = Date.now();
            },
            false,
            { type: "requestCameraReset" }
          ),

        requestFrameObject: () =>
          set(
            (state) => {
              state.frameObjectAt = Date.now();
            },
            false,
            { type: "requestFrameObject" }
          ),

        clearScene: () =>
          set(
            (state) => {
              state.visibleFileIds = [];
              state.selectedFileId = null;
              state.sliceFileId = null;
              state.visualizationError = null;
              state.previewAsset = null;
              state.comparison = null;
            },
            false,
            { type: "clearScene" }
          ),

        addFile: (file) =>
          set(
            (state) => {
              state.files[file.id] = file;
            },
            false,
            { type: "addFile", fileId: file.id }
          ),

        showFile: (file) =>
          set(
            (state) => {
              state.files[file.id] = file;
              if (!state.visibleFileIds.includes(file.id)) state.visibleFileIds.push(file.id);
            },
            false,
            { type: "showFile", fileId: file.id }
          ),

        setSliceFile: (file) =>
          set(
            (state) => {
              state.visualizationError = null;
              if (file === null) {
                if (state.sliceFileId) {
                  const fileId = state.sliceFileId;
                  delete state.files[fileId];
                  state.visibleFileIds = state.visibleFileIds.filter((id) => id !== fileId);
                  if (state.selectedFileId === fileId) {
                    state.selectedFileId = null;
                  }
                }
                state.sliceFileId = null;
                return;
              }

              if (state.sliceFileId && state.sliceFileId !== file.id) {
                const previousSliceId = state.sliceFileId;
                delete state.files[previousSliceId];
                state.visibleFileIds = state.visibleFileIds.filter((id) => id !== previousSliceId);
              }

              state.files[file.id] = file;
              // Seeding is exclusive: this file is what the step renders, so it is the only
              // thing visible. Appending let anything already shown — an MCP show-file, a
              // restored `vf` id — stay on screen beside it, including files of this very part
              // and operation that scoping cannot separate (SW-1676). Nothing else in the app
              // toggles visibility from the UI, so no user choice is being overridden here.
              state.visibleFileIds = [file.id];
              state.selectedFileId = file.id;
              state.sliceFileId = file.id;
            },
            false,
            { type: "setSliceFile", fileId: file?.id ?? null }
          ),

        toggleFileVisibility: (fileId) =>
          set(
            (state) => {
              if (state.files[fileId]) {
                if (state.visibleFileIds.includes(fileId)) {
                  state.visibleFileIds = state.visibleFileIds.filter((id) => id !== fileId);
                  if (state.sliceFileId === fileId) {
                    state.sliceFileId = null;
                  }
                  if (state.selectedFileId === fileId) {
                    state.selectedFileId = null;
                  }
                } else {
                  state.visibleFileIds.push(fileId);
                }
              }
            },
            false,
            { type: "toggleFileVisibility", fileId }
          ),

        selectFile: (fileId) =>
          set(
            (state) => {
              if (state.files[fileId] && state.visibleFileIds.includes(fileId)) {
                state.selectedFileId = fileId;
              }
            },
            false,
            { type: "selectFile", fileId }
          ),

        deselectFile: () =>
          set(
            (state) => {
              state.selectedFileId = null;
            },
            false,
            { type: "deselectFile" }
          ),

        toggleSelectFile: (fileId) =>
          set(
            (state) => {
              const isVisibleFile =
                Boolean(state.files[fileId]) && state.visibleFileIds.includes(fileId);
              if (!isVisibleFile) {
                return;
              }

              const isCurrentlySelected = state.selectedFileId === fileId;
              const willBeSelected = !isCurrentlySelected;
              state.selectedFileId = willBeSelected ? fileId : null;
            },
            false,
            { type: "toggleSelectFile", fileId }
          ),

        setScope: (scope) =>
          set(
            (state) => {
              dropFiles(
                state,
                Object.keys(state.files).filter((id) => !isFileInScope(state.files[id], scope))
              );
            },
            false,
            { type: "setScope", ...scope }
          ),

        removeFile: (fileId) =>
          set(
            (state) => {
              delete state.files[fileId];
              state.visibleFileIds = state.visibleFileIds.filter((id) => id !== fileId);
              const wasSelected = state.selectedFileId === fileId;
              if (state.sliceFileId === fileId) {
                state.sliceFileId = null;
              }
              if (wasSelected) {
                state.selectedFileId = null;
              }
            },
            false,
            { type: "removeFile", fileId }
          ),

        reset: () =>
          set(
            (state) => {
              state.files = {};
              state.visibleFileIds = [];
              state.selectedFileId = null;
              state.sliceFileId = null;
              state.visualizationError = null;
              state.displayAsset = null;
              state.resolvedAsset = null;
              state.previewAsset = null;
              state.seamDrawing = false;
              state.seamStrokeActive = false;
              state.seamDrawAlignAt = null;
              state.seamFences = [];
              state.seamFencesVisible = true;
              state.hoveredSeamFenceId = null;
              state.seamDrawFootprint = null;
              state.seamDrawGeometry = null;
              state.sliceLayers = DEFAULT_SLICE_LAYERS;
              state.slicePreviewAvailable = false;
              state.slicePreviewStale = false;
            },
            false,
            { type: "reset" }
          ),

        setVisualizationError: (error) =>
          set(
            (state) => {
              state.visualizationError = error;
            },
            false,
            { type: "setVisualizationError", fileId: error?.fileId ?? null }
          ),

        setHoveredRegionIndex: (index) =>
          set(
            (state) => {
              state.hoveredRegionIndex = index;
            },
            false,
            { type: "setHoveredRegionIndex", index }
          ),

        setFocusedRegionIndex: (index) =>
          set(
            (state) => {
              state.focusedRegionIndex = index;
            },
            false,
            { type: "setFocusedRegionIndex", index }
          ),

        setDisplayAsset: (asset) =>
          set(
            (state) => {
              state.displayAsset = asset;
            },
            false,
            { type: "setDisplayAsset", asset }
          ),

        setResolvedAsset: (asset) =>
          set(
            (state) => {
              state.resolvedAsset = asset;
            },
            false,
            { type: "setResolvedAsset", render: asset?.render ?? null }
          ),

        setPreviewAsset: (asset) =>
          set(
            (state) => {
              state.previewAsset = asset;
            },
            false,
            { type: "setPreviewAsset", kind: asset?.kind ?? null }
          ),

        setViewportCovered: (covered) =>
          set(
            (state) => {
              state.viewportCovered = covered;
            },
            false,
            { type: "setViewportCovered", covered }
          ),

        setComparison: (assets) =>
          set(
            (state) => {
              state.comparison = assets;
            },
            false,
            { type: "setComparison", count: assets?.length ?? 0 }
          ),
      }),
      { name: "Viewer3DStore", trace: process.env.NODE_ENV === "development" }
    )
  )
);

type Viewer3DState = ReturnType<typeof useViewer3DStore.getState>;

/**
 * The STL files the given scope may draw. Scoping here — not only when files are added — is what
 * keeps a leftover mesh off the screen no matter how it reached the store: a stale `vf` URL param
 * resurrects ids the current step never seeded.
 */
export function selectStlFiles(state: Viewer3DState, scope: SceneScope): SceneFile[] {
  return Object.values(state.files).filter(
    (file) => hasFileExtension(file.name, "stl") && isFileInScope(file, scope)
  );
}
